import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { AuthManager, useAuthManager } from "../hooks/useAuthManager";
import { useDispatch } from "react-redux";
import { authenticateTwoFactor, setCurrentUser, setIsAuthLoading } from "../authSlice";
import { isPAPLUsers } from "utils/helpers";
import { apiV1 } from "api-v2";
import { User } from "../types/User.type";
import { navigate } from "@reach/router";
import { getOrFetchXsrfToken } from "../utils";

export interface AuthenticateValues {
	[key: string]: unknown;
	email: string;
	invitetoken?: string | null;
	password: string;
	remember_me?: boolean;
}

export interface AuthenticateResponse {
	company_admin: boolean;
	expires_in: number;
	is_admin: boolean;
	success: boolean;
	token_type: string;
	user: User;
}

interface AuthContextType extends AuthManager {
	authenticateUser: (
		authData: AuthenticateResponse
	) => Promise<AuthenticateResponse>;
	loginUser: (values: AuthenticateValues) => Promise<AuthenticateResponse>;
}

export const AuthContext = createContext<AuthContextType>(null);

export const AuthContextProvider = ({ children }) => {
	const { errorMessage, isSuspended, isVerified, logoutUser } = useAuthManager();

	const dispatch = useDispatch();

	const authenticateUser = useCallback(
		async (authData: AuthenticateResponse): Promise<AuthenticateResponse> => {
			try {
				let user = authData?.user;

				// authenticate user in store
				await dispatch(
					authenticateTwoFactor({
						two_factor_enabled: user?.two_factor_enabled,
						two_factor_verified: false,
					})
				);

				/* -------------------------------------------------------------- */
				/* Two-factor enabled                                             */
				/* -------------------------------------------------------------- */

				if (user?.two_factor_enabled) {
					localStorage.setItem("two_factor_enabled", "true");
					localStorage.setItem("two_factor_verified", "false");

					navigate("/two-factor");
					return;
				}

				/* -------------------------------------------------------------- */
				/* No Two-factor                                                 */
				/* -------------------------------------------------------------- */

				localStorage.setItem("two_factor_enabled", "false");
				localStorage.setItem("two_factor_verified", "false");

				if (isPAPLUsers(user)) {
					navigate("/two-factor-setup");
					return;
				}

				if (!user.company && !user.is_guest) {
					const userProfileResponse = await apiV1.get("auth/user-profile");
					user = userProfileResponse?.data?.data;
				}

				localStorage.setItem(
					"sidebar",
					JSON.stringify({ sidebarcollapse: false })
				);

				dispatch(setCurrentUser(user));

				return authData;
			} catch (error) {
				console.error("Authentication failed:", error);
				throw error;
			}
		},
		[dispatch]
	);

	const loginUser = useCallback(
		async (values: AuthenticateValues): Promise<AuthenticateResponse> => {
			try {
				dispatch(setIsAuthLoading(true));
				await getOrFetchXsrfToken();
				const userResponse = await apiV1.post<AuthenticateResponse>(
					"auth/login",
					{
						...values,
					}
				);

				if (!userResponse.data?.success) {
					userResponse.data;
				}

				return await authenticateUser(userResponse.data);
			} catch (error) {
				console.error("Login failed:", error);
				throw error;
			} finally {
				dispatch(setIsAuthLoading(false));
			}
		},
		[authenticateUser, dispatch]
	);

	return (
		<AuthContext.Provider
			value={{
				errorMessage,
				isSuspended,
				isVerified,
				authenticateUser,
				loginUser,
				logoutUser
			}}>
			{children}
		</AuthContext.Provider>
	);
};

export const useAuthContext = (): AuthContextType => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuthContext must be used within AuthContextProvider");
	}
	return context;
};
